import { Text, useDisclosure } from '@chakra-ui/react';
import { CreditCardIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionStepStatus } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  MergedPaymentStep,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';

import { SubscriptionStep } from '../../SubscriptionStep';
import { SubscriptionBankDetailsModal } from './SubscriptionBankDetailsModal';

type Props = {
  subscriptionStep: MergedPaymentStep;
  isDisabled: boolean;
};

export function PaymentStep({ subscriptionStep, isDisabled }: Props): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { subscriptionId = '' } = useParams();

  const { data } = useGetSubscriptionQuery({ variables: { subscriptionId } });
  const subscription = data?.subscription;
  const investAmount = subscription?.investAmount;
  const fundraising = subscription?.fundraising;
  const isAfterClosing = !!subscriptionStep.isAfterClosing;
  const fundraisingIsReviewed = fundraising?.status === FundraisingStatus.REVIEWED;

  /* ** Fund values ** */
  const fundsTransferValue = formatNumberCurrency(subscription?.bankVirtualIban?.balance || 0);
  const fundsTransferTotal = formatNumberCurrency(subscription?.amountDue || 0);

  const isInvestAmount = !!investAmount && investAmount !== 0;

  const handleFundsTransferModalOpen = (status: SubscriptionStepStatus): void => {
    if (status !== SubscriptionStepStatus.VALID) {
      onOpen();
    }
  };

  const getStatusMessage = (stepStatus: SubscriptionStepStatus): React.ReactNode => {
    switch (stepStatus) {
      case SubscriptionStepStatus.VALID:
        return i18nSubscription.rich('FundTransferSubtitleSuccess', {
          fundsTransferTotal,
          important: (chunks) => <Text as="b">{chunks}</Text>,
        });
      case SubscriptionStepStatus.ERROR:
        return i18nSubscription.rich('FundTransferSubtitleFailure', {
          fundsTransferValue,
          fundsTransferTotal,
          important: (chunks) => <Text as="b">{chunks}</Text>,
        });
      default:
        return i18nSubscription.rich('FundTransferSubtitlePending', {
          fundsTransferTotal,
          important: (chunks) => <Text as="b">{chunks}</Text>,
        });
    }
  };

  const isStepDisabled = isDisabled || (isAfterClosing && !fundraisingIsReviewed);

  return (
    <>
      {isAfterClosing && (
        <Text fontSize="sm" fontWeight="600" mt="2">
          {i18nSubscription('StepAfterClosing')}
        </Text>
      )}
      <SubscriptionStep
        icon={CreditCardIcon}
        isDisabled={isStepDisabled}
        status={subscriptionStep.status}
        title={i18nSubscription('FundsTransfer')}
        type={subscriptionStep.type}
        onClick={handleFundsTransferModalOpen}
      >
        {isInvestAmount && <Text fontSize="sm">{getStatusMessage(subscriptionStep.status)}</Text>}
      </SubscriptionStep>
      <SubscriptionBankDetailsModal isOpen={isOpen} onClose={onClose} />
    </>
  );
}
